/**
 * WalletInstructionsLabel — HTML overlay that teaches wallet gestures.
 *
 * Shows two lines of text with a right-pointing arrow aimed at the wallet.
 * Two gesture flags are tracked:
 *   - tap-to-pull: player tapped a bill to commit it
 *   - hold-to-swap: player tap-held to open the radial denomination menu
 *
 * Once BOTH flags are satisfied, the label is dismissed and never re-shown
 * (persisted in localStorage so veterans aren't nagged across sessions).
 *
 * HTML-based (not canvas/3D) so the strings can be swapped for localization
 * later without touching rendering code.
 */

import { gsap } from 'gsap';

export interface WalletInstructionsLabelOptions {
  container: HTMLElement;
}

const STORAGE_KEY = 'sdl_wallet_tutorial_v1';

interface TutorialFlags {
  tapUsed: boolean;
  holdUsed: boolean;
}

export class WalletInstructionsLabel {
  private root: HTMLElement;
  private tapLine: HTMLElement;
  private holdLine: HTMLElement;
  private flags: TutorialFlags;
  private isShown = false;
  private disabled = false;

  constructor(options: WalletInstructionsLabelOptions) {
    WalletInstructionsLabel.injectStyles();
    this.flags = this.loadFlags();

    this.root = document.createElement('div');
    this.root.className = 'wallet-instructions';
    this.root.style.display = 'none';

    const textEl = document.createElement('div');
    textEl.className = 'wallet-instructions__text';

    this.tapLine = document.createElement('div');
    this.tapLine.className = 'wallet-instructions__line';
    this.tapLine.textContent = 'TAP TO PULL A BILL';

    this.holdLine = document.createElement('div');
    this.holdLine.className = 'wallet-instructions__line';
    this.holdLine.textContent = 'HOLD TO SWAP';

    // Hide lines whose gesture was already learned in a prior session, so the
    // label shows only the remaining instruction(s) on first reveal.
    if (this.flags.tapUsed) this.tapLine.style.display = 'none';
    if (this.flags.holdUsed) this.holdLine.style.display = 'none';

    textEl.appendChild(this.tapLine);
    textEl.appendChild(this.holdLine);

    const arrow = document.createElement('div');
    arrow.className = 'wallet-instructions__arrow';

    this.root.appendChild(textEl);
    this.root.appendChild(arrow);
    options.container.appendChild(this.root);
  }

  /**
   * Permanently suppress this label. Used by the guided tutorial, where the
   * coach teaches the tap/hold gestures step-by-step — the auto-popping hints
   * would double up on the coach banner and confuse the player. Hides any
   * current label and no-ops every future show().
   */
  disable(): void {
    this.disabled = true;
    this.hide();
  }

  /** Show the label if the player hasn't graduated yet. Idempotent. */
  show(): void {
    if (this.disabled || this.isGraduated() || this.isShown) return;
    this.isShown = true;
    this.root.style.display = 'flex';
    this.root.style.transformOrigin = 'left center';
    // Push the label inward by 25% of its own width so it doesn't sit flush
    // against the left edge. Done via gsap.set (not CSS) so the scale-in
    // animation below — which gsap drives through the same transform string
    // — keeps the offset instead of overwriting it.
    gsap.set(this.root, { x: '25%' });
    gsap.fromTo(
      this.root,
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' }
    );
  }

  /** Hide the label (fade out). Idempotent. */
  hide(): void {
    if (!this.isShown) return;
    this.isShown = false;
    gsap.to(this.root, {
      opacity: 0,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        this.root.style.display = 'none';
      },
    });
  }

  /** Record that the player tapped a bill (short press). */
  markTapUsed(): void {
    if (this.flags.tapUsed) return;
    this.flags.tapUsed = true;
    this.persistFlags();
    this.collapseLine(this.tapLine);
    this.maybeGraduate();
  }

  /** Record that the player tap-held to change denomination. */
  markHoldUsed(): void {
    if (this.flags.holdUsed) return;
    this.flags.holdUsed = true;
    this.persistFlags();
    this.collapseLine(this.holdLine);
    this.maybeGraduate();
  }

  /** Has the player demonstrated both gestures? */
  isGraduated(): boolean {
    return this.flags.tapUsed && this.flags.holdUsed;
  }

  destroy(): void {
    gsap.killTweensOf(this.root);
    this.root.remove();
  }

  // ── internals ──────────────────────────────────────────────────────

  private collapseLine(line: HTMLElement): void {
    if (line.style.display === 'none') return;
    gsap.to(line, {
      opacity: 0,
      height: 0,
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        line.style.display = 'none';
      },
    });
  }

  private maybeGraduate(): void {
    if (this.isGraduated()) this.hide();
  }

  private loadFlags(): TutorialFlags {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<TutorialFlags>;
        return { tapUsed: !!parsed.tapUsed, holdUsed: !!parsed.holdUsed };
      }
    } catch { /* localStorage unavailable */ }
    return { tapUsed: false, holdUsed: false };
  }

  private persistFlags(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.flags));
    } catch { /* noop */ }
  }

  private static stylesInjected = false;
  private static injectStyles(): void {
    if (WalletInstructionsLabel.stylesInjected) return;
    WalletInstructionsLabel.stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = `
      .wallet-instructions {
        position: fixed;
        left: 16px;
        bottom: 120px;
        display: flex;
        align-items: stretch;
        pointer-events: none;
        z-index: 55;
        font-family: 'Bebas Neue', 'Rajdhani', sans-serif;
        will-change: transform, opacity;
        /* Kill inter-element whitespace so the triangle sits flush with the box */
        font-size: 0;
      }
      .wallet-instructions__text {
        background: #000000;
        color: #FFFFFF;
        padding: 14px 18px;
        font-size: 16px;
        line-height: 1.25;
        letter-spacing: 0.06em;
        white-space: nowrap;
      }
      .wallet-instructions__line {
        display: block;
      }
      .wallet-instructions__line + .wallet-instructions__line {
        margin-top: 6px;
      }
      .wallet-instructions__line::before {
        content: '\\2022';
        display: inline-block;
        margin-right: 6px;
        color: #FFFFFF;
      }
      .wallet-instructions__arrow {
        /* Sized to ~75% of the text block height so the arrow reads as a
           proportional tip, not dominating the shape. Overlaps 1px to close
           any subpixel gap between the box edge and the triangle base. */
        width: 0;
        height: 0;
        border-top: 21px solid transparent;
        border-bottom: 21px solid transparent;
        border-left: 16.5px solid #000000;
        margin-left: -1px;
        align-self: center;
      }
    `;
    document.head.appendChild(style);
  }
}
